import {
  X,
  User,
  Users2,
  Newspaper,
  GraduationCap,
  Clock,
  MessageSquare,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

interface Report {
  id: number;
  type: 'USER' | 'SQUAD' | 'ARTICLE' | 'PROVIDER';
  status: string;
  reporterId: number;
  reporterName: string;
  reporterEmail: string;
  targetId: number;
  targetName: string;
  targetType: string;
  reasonCode: string;
  reasonText?: string;
  description?: string;
  contextType?: string;
  contextThreadId?: number;
  contextMessageCursor?: string;
  createdAt: string;
  reviewedAt?: string;
  reviewedById?: number;
  reviewedByName?: string;
  reviewNotes?: string;
}

interface ReportDetailPanelProps {
  report: Report;
  onClose: () => void;
  onAction: (action: string) => void;
  isActioning?: boolean;
}

export function ReportDetailPanel({
  report,
  onClose,
  onAction,
  isActioning = false,
}: ReportDetailPanelProps) {
  // Get icon and color based on report type
  const getTypeConfig = () => {
    switch (report.type) {
      case 'USER':
        return { icon: User, color: 'text-blue-400', bg: 'bg-blue-500/10' };
      case 'SQUAD':
        return { icon: Users2, color: 'text-purple-400', bg: 'bg-purple-500/10' };
      case 'ARTICLE':
        return { icon: Newspaper, color: 'text-green-400', bg: 'bg-green-500/10' };
      case 'PROVIDER':
        return { icon: GraduationCap, color: 'text-amber-400', bg: 'bg-amber-500/10' };
      default:
        return { icon: AlertTriangle, color: 'text-slate-400', bg: 'bg-slate-500/10' };
    }
  };

  const { icon: TypeIcon, color, bg } = getTypeConfig();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const isPending = report.status === 'PENDING';

  return (
    <div className="flex flex-col h-full bg-slate-900 border-l border-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${bg}`}>
            <TypeIcon className={`h-4 w-4 ${color}`} />
          </div>
          <span className="font-medium text-white">Report Details</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Target Info */}
        <div>
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            Reported {report.targetType}
          </h3>
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <div>
              <div className="font-medium text-white">{report.targetName}</div>
              <div className="text-sm text-slate-400">ID: {report.targetId}</div>
            </div>
            <button className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white">
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Reporter Info */}
        <div>
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            Reported By
          </h3>
          <div className="p-3 bg-slate-800/50 rounded-lg">
            <div className="font-medium text-white">{report.reporterName}</div>
            <div className="text-sm text-slate-400">{report.reporterEmail}</div>
            <div className="text-xs text-slate-500 mt-1">ID: {report.reporterId}</div>
          </div>
        </div>

        {/* Reason */}
        <div>
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            Reason
          </h3>
          <div className="p-3 bg-slate-800/50 rounded-lg">
            <div className="inline-block px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-sm font-medium mb-2">
              {report.reasonCode}
            </div>
            {report.reasonText && (
              <p className="text-slate-300">{report.reasonText}</p>
            )}
            {report.description && (
              <p className="text-slate-300 mt-2">{report.description}</p>
            )}
          </div>
        </div>

        {/* Context */}
        {report.contextType && (
          <div>
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
              Context
            </h3>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4 text-cyan-400" />
                <span className="text-sm font-medium text-white">{report.contextType} Report</span>
              </div>
              {report.contextThreadId && (
                <div className="text-sm text-slate-400">Thread ID: {report.contextThreadId}</div>
              )}
              {report.contextMessageCursor && (
                <div className="text-xs text-slate-500 mt-1 font-mono break-all">
                  Message: {report.contextMessageCursor}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div>
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            Timeline
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-slate-500" />
              <div>
                <div className="text-sm text-white">Created</div>
                <div className="text-xs text-slate-400">{formatDate(report.createdAt)}</div>
              </div>
            </div>
            {report.reviewedAt && (
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <div>
                  <div className="text-sm text-white">Reviewed by {report.reviewedByName}</div>
                  <div className="text-xs text-slate-400">{formatDate(report.reviewedAt)}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Review Notes */}
        {report.reviewNotes && (
          <div>
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
              Review Notes
            </h3>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-slate-300 whitespace-pre-wrap">{report.reviewNotes}</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      {isPending && (
        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-500 mb-3">Take Action</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onAction('DISMISS')}
              disabled={isActioning}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={() => onAction('WARN')}
              disabled={isActioning}
              className="px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              Warn
            </button>
            <button
              onClick={() => onAction('SUSPEND_7D')}
              disabled={isActioning}
              className="px-3 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              Suspend 7d
            </button>
            <button
              onClick={() => onAction('SUSPEND_30D')}
              disabled={isActioning}
              className="px-3 py-2 bg-orange-700 hover:bg-orange-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              Suspend 30d
            </button>
            <button
              onClick={() => onAction('BAN')}
              disabled={isActioning}
              className="col-span-2 px-3 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              Permanent Ban
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
